import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { bulkUploadContent } from '../services/contentService';
import { useAuth } from '../context/AuthContext';

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState([]);
    const { token } = useAuth();

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                toast.error('Please select a CSV file');
                return;
            }
            setFile(selectedFile);
            parseCSV(selectedFile);
        }
    };

    // Proper CSV parser that handles quoted fields with commas
    const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Double quote escape
                    current += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                // End of field
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        
        // Add final field
        result.push(current.trim());
        return result;
    };

    // Enhanced CSV parser that handles multi-line content within quotes
    const parseCSVContent = (text) => {
        const rows = [];
        let currentRow = '';
        let inQuotes = false;
        
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const nextChar = text[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    currentRow += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                    currentRow += char;
                }
            } else if (char === '\n' && !inQuotes) {
                // End of row (not inside quotes)
                if (currentRow.trim()) {
                    rows.push(currentRow.trim());
                }
                currentRow = '';
            } else {
                currentRow += char;
            }
        }
        
        // Add final row
        if (currentRow.trim()) {
            rows.push(currentRow.trim());
        }
        
        return rows;
    };

    const parseCSV = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const rows = parseCSVContent(text);
            
            if (rows.length === 0) return;
            
            const headers = parseCSVLine(rows[0]);
            
            const data = [];
            for (let i = 1; i < rows.length && i < 6; i++) { // Preview first 5 rows
                const values = parseCSVLine(rows[i]);
                const row = {};
                headers.forEach((header, index) => {
                    row[header] = values[index] || '';
                });
                data.push(row);
            }
            setPreview(data);
        };
        reader.readAsText(file);
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file');
            return;
        }

        setUploading(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target.result;
                const rows = parseCSVContent(text);
                
                if (rows.length === 0) {
                    toast.error('CSV file is empty');
                    return;
                }
                
                const headers = parseCSVLine(rows[0]);
                
                const contentData = [];
                for (let i = 1; i < rows.length; i++) {
                    const values = parseCSVLine(rows[i]);
                    const row = {};
                    headers.forEach((header, index) => {
                        row[header] = values[index] || '';
                    });
                    
                    // Map CSV columns to content fields
                    const contentItem = {
                        title: row.title || row.Title,
                        slug: row.slug || row.Slug,
                        eyebrow: row.eyebrow || row.Eyebrow || row.Category,
                        description: row.description || row.Description,
                        content: row.content || row.Content,
                        image: row.image || row.Image || row['Image URL'],
                        contentType: row.contentType || row['Content Type'] || 'blog',
                        status: row.status || row.Status || 'published',
                        metaTitle: row.metaTitle || row['Meta Title'],
                        metaDescription: row.metaDescription || row['Meta Description'],
                        tags: row.tags ? row.tags.split(';').map(t => t.trim()) : [],
                        readTime: row.readTime || row['Read Time']
                    };

                    // Debug logging to see what's being parsed
                    console.log(`Row ${i} parsed:`, {
                        title: contentItem.title,
                        content: contentItem.content?.substring(0, 100) + '...',
                        fieldsCount: Object.keys(row).length,
                        headers: headers
                    });
                    
                    contentData.push(contentItem);
                }

                const result = await bulkUploadContent(contentData, token);
                
                if (result.success) {
                    toast.success(`Successfully uploaded ${result.data.createdCount} of ${result.data.totalRows} items`);
                    if (result.data.errors.length > 0) {
                        toast.warning(`${result.data.errors.length} items had errors. Check console for details.`);
                        console.log('Upload errors:', result.data.errors);
                    }
                    onSuccess();
                    onClose();
                } else {
                    toast.error('Upload failed');
                }
            };
            reader.readAsText(file);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const downloadTemplate = () => {
        const csvContent = `title,slug,eyebrow,description,content,image,contentType,status,metaTitle,metaDescription,tags,readTime
"Sample Blog Post: Ultimate Guide","sample-blog-post-guide","Skincare","This is a comprehensive guide to skincare routines and best practices.","<h2>Introduction</h2><p>Welcome to our comprehensive skincare guide. In this article, we'll cover everything you need to know about building an effective skincare routine.</p><h2>Morning Routine</h2><p>Your morning routine should focus on protection and hydration. Start with a gentle cleanser, followed by a vitamin C serum.</p><h2>Evening Routine</h2><p>Evening is the time for repair and renewal. Use products with retinol or peptides to help your skin regenerate overnight.</p>","/images/hero-face.png","blog","published","Ultimate Skincare Guide - Sample Post","Learn the best skincare practices with our comprehensive guide covering morning and evening routines.","skincare;guide;routine;beauty","8 min read"
"Another Sample Post","another-sample-post","Beauty","Short description for the second post.","<h2>Second Post Content</h2><p>This is the content for the second sample post.</p>","/images/skincare-woman.png","blog","published","Second Sample Post","Meta description for second post","beauty;tips","3 min read"`;
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'blog-content-template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#f8f8f8] rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Bulk Upload Content</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
                        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                            <li>Download the CSV template below</li>
                            <li>Fill in your content data (title and content are required)</li>
                            <li><strong>Important:</strong> Keep HTML content properly quoted in CSV</li>
                            <li>Use semicolons (;) to separate multiple tags</li>
                            <li>Upload the completed CSV file</li>
                            <li>Review the preview and click Upload</li>
                        </ol>
                        <div className="mt-2 p-2 bg-blue-100 rounded text-xs text-blue-700">
                            <strong>Tip:</strong> For content with HTML tags, commas, or quotes, make sure the entire field is wrapped in double quotes.
                        </div>
                    </div>

                    {/* Template Download */}
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                            <h4 className="font-medium text-gray-900">CSV Template</h4>
                            <p className="text-sm text-gray-600">Download the template with all required columns</p>
                        </div>
                        <button
                            onClick={downloadTemplate}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Download Template
                        </button>
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select CSV File
                        </label>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Preview */}
                    {preview.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">Preview (First 5 rows)</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-200 rounded-lg">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {preview.map((row, index) => (
                                            <tr key={index}>
                                                <td className="px-3 py-2 text-sm text-gray-900">{row.title || row.Title}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600">{row.contentType || row['Content Type'] || 'blog'}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600">{row.status || row.Status || 'published'}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate">{row.description || row.Description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-4">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={!file || uploading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? 'Uploading...' : 'Upload Content'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkUploadModal;
